import { ActionTree } from "@service/store/types/Store";
import { RootState } from "@service/store/types/RootState";
import { sessionKey, interfaceStateKey } from "../../helpers/sessionHelper";
import { UserProfile } from "@api/models/UserProfile";
import i18 from '../../locale/i18';
import { loginByToken } from "@api/helpers/relogin";
import {repositories} from "../../api/Repositories";
import {RepositoryFabric} from "@api/repositories/RepositoryFabric";
import {SettingRepository} from "@api/repositories/SettingRepository";
import {UserRepository} from "@api/repositories/UserRepository";
import { SignalRMethod } from "@api/repositories/reatime-repository/types";
import {RealtimeRepository} from "@api/repositories/reatime-repository/RealtimeRepository";
import {saveOffset, getOffset} from "../../helpers/offsetHelper";
import {Settings} from "luxon";
import * as Sentry from '@sentry/browser';
import store from "../index";
import {DateTime} from "luxon";
import {removeWelcomeTourDone} from "../../helpers/selectedEventHelper";
import { AnalyticsScreenName } from "@service/types/AnalyticsScreenName";
import { parseJson } from '@common/TypesHelper';
import { getImageUrl } from '@common/ImageUrlHelper';
import { changeFavicon } from "@common/htmlHelpers";
import { client } from "@api/clients/web";
import { getBaseUrl } from "@api/helpers/url/baseUrl";
import {fetchFavoritesAction, initObserverAction} from "@service/store/favorites/actions";
import { getLSKey as getTimezoneLSKey, getTimezoneFromLS, setTimezoneToLS } from "@share/session-edit/helpers/timeZoneHelper";
import { chooseInitDate } from "@share/program/helpers/chooseInitDate";

export const mainActions = {
    setSession: "setSession",
    setSessionProfile: "setSessionProfile",
    setSessionLanguage: "setSessionLanguage",
    setNewCredentials: "setNewCredentials",
    setSessionFromLocalStorage: "setSessionFromLocalStorage",
    resetSession: "resetSession",
    showSpinner: "showSpinner",
    hideSpinner: "hideSpinner",
    saveScreenNameData: "saveScreenNameData",
    fetchInitData: "fetchInitData",
    setPageTitle: "setPageTitle",
    setFavicon: "setFavicon",
    relogin: "relogin",
    fetchTimezone: "fetchTimezone",
    reportException: "reportException",
    reportError: "reportError",
    startProfileReloading: "startProfileReloading",
    startConfigurationReloading: "startConfigurationReloading",
    stopMeetingsChecking: "stopMeetingsChecking",
    stopProfileReloading: "stopProfileReloading",
    stopConfigurationReloading: "stopConfigurationReloading",
    clearIntervals: "clearIntervals",
    removeLocalStorageItems: "removeLocalStorageItems",
    setWebConferenceRecordingEventId: "setWebConferenceRecordingEventId",
    addInitError: "addInitError",
    setIntefaceState: "setIntefaceState",
    toggleIsOnEditing: "toggleIsOnEditing",
    onIsOnEditing: "onIsOnEditing",
    offIsOnEditing: "offIsOnEditing",
    setInterfaceStateToLS: "setInterfaceStateToLS",
    setCurrentUserCompanyId: "setCurrentUserCompanyId",
    setSelectedTimeZone: "setSelectedTimeZone",
    initSelectedTimeZone: "initSelectedTimeZone",
    setAnonymAuthVisibility: "setAnonymAuthVisibility"
} as const;

export const actions: ActionTree<RootState, RootState, typeof mainActions> = {
    setSession({ commit }, {data, backend}) {
        (client as any).setIsWrongRefreshToken(false);
        localStorage.setItem(sessionKey(backend), JSON.stringify(data));
        if (data.profile) {
            data.profile = new UserProfile(data.profile);
        }
        commit('setSession', data);
    },
    setSessionProfile({ commit, state }, data: UserProfile) {
        commit('setSessionProfile', data);
        localStorage.setItem(sessionKey(), JSON.stringify(state.session));
    },
    setSessionLanguage({commit, state}, language: string | null) {
        if(language) {
            commit('setSessionLanguage', language);
            localStorage.setItem(sessionKey(), JSON.stringify(state.session));
            i18.locale = language;
            Settings.defaultLocale = language;
        }
    },
    setNewCredentials({ state, dispatch }, { sessionId, refreshToken, lastRefreshDate}) {
        const newSession = Object.assign({}, state.session, {
            sessionId, refreshToken, lastRefreshDate
        })
        dispatch('setSession', { data: newSession, backend: state.session.backend });
    },

    setSessionFromLocalStorage({ state, commit }) {
        const storageSession = localStorage.getItem(sessionKey(state.session.backend)) || '{}';
        const session = JSON.parse(storageSession);
        if (session.profile) {
            session.profile = new UserProfile(session.profile);
        }
        commit('setSession', session);
    },

    async resetSession({ commit, dispatch, rootState }, backend) {
        await (repositories.get(RepositoryFabric.REALTIME) as RealtimeRepository).clear();
        dispatch('removeLocalStorageItems');
        removeWelcomeTourDone();
        dispatch('clearIntervals');
        commit('formModal/setElements', []);
        dispatch('program/reset');
        commit('services/clearServiceParams', { backend });
        commit('events/resetIsLoaded');
        if ((rootState as any).services.freshChat) {
            dispatch('services/destroyFreshChat', null, {root: true});
        }
        localStorage.removeItem(sessionKey(backend));
        commit('resetSession');
    },
    showSpinner({commit}) {
        commit('setIsLoading', true);
    },
    hideSpinner({commit}) {
        commit('setIsLoading', false);
    },
    saveScreenNameData({state, commit}, value: string) {

        const screenNameData: AnalyticsScreenName = { prevName: "", currentName: value };

        if (!state.screenNameData) {
            commit('setScreenNameData', screenNameData);
        }
        else {
            screenNameData.prevName = state.screenNameData.currentName;
            commit('setScreenNameData', screenNameData);
        }
    },
    async fetchInitData({state, dispatch, rootGetters, rootState, commit}, data) {
        commit('setInitializing', true);
        dispatch(mainActions.setIntefaceState);
        try {
            dispatch(mainActions.initSelectedTimeZone);
            dispatch('startConfigurationReloading', data.backend)
            await dispatch('events/load', null, {root: true});
            dispatch('events/setInitSelected', null, {root: true});
            await dispatch('fetchTimezone');

            const dates = rootGetters['events/selectedEventViewParam']('dates', null);
            const date = chooseInitDate(rootGetters['events/selectedEvent'], (rootState as any).timeZoneOffset, dates);

            await Promise.all([
                dispatch('startProfileReloading'),
                dispatch('onlineUsers/startTracking'),
                dispatch('program/initialize', {repositories, date: date, store}),
                dispatch(fetchFavoritesAction, process.env.VUE_APP_AGENDA_SCHEMA),
            ]);

            dispatch(initObserverAction, store);

            if (rootGetters['events/selectedEventViewParam']('points', null)?.isShow) {
                await dispatch('points/startTracking');
            }

            const supportChatParams = rootGetters['services/getServiceParam']('supportChat', 'auth')
            if (!supportChatParams || (!supportChatParams['supporterUserId'] && rootGetters['services/freshChatToken'])) {
                if (!(rootState as any).services.freshChat) {
                    dispatch('services/initFreshChat', true, {root: true});
                } else {
                    dispatch('services/setFreshChatUserData', null, {root: true});
                }
                if (Object.prototype.hasOwnProperty.call(data, 'afterLogin') && data.afterLogin) {
                    dispatch('services/freshChatFireAfterLogin', 'auth', {root: true});
                }
            } else if ((rootState as any).services.freshChat) {
                dispatch('services/destroyFreshChat', null, {root: true});
            }

            dispatch("setFavicon", state.session.backend);
        }
        finally {
            commit('setInitializing', false);
        }
    },
    setPageTitle({ getters }, itemTitle: string) {
        const projectName = getters["services/getServiceParam"]('webClientPageParams', 'projectName');
        const eventTitle = getters["events/selectedEvent"]?.title;
        const secondPart = [eventTitle, projectName].filter(item => item).join(' - ');

        document.title = [itemTitle, secondPart].filter(item => item).join(' - ');
    },
    setFavicon({ getters }, backend: string) {
        const imageFileId = getters["services/getServiceParam"]('webClientPageParams', 'faviconFileId');

        if (imageFileId && backend) {
            const url = getImageUrl(backend, imageFileId);
            changeFavicon(url);
        }
        else {
            changeFavicon(`${location.origin}/app-icon/favicon.ico`);
        }
    },
    async relogin({state, dispatch}) {
        try {
            const newSession = await loginByToken(getBaseUrl(state.session.backend), state.session.refreshToken);
            dispatch('setNewCredentials', {
                sessionId: newSession.sessionId,
                refreshToken: newSession.refreshToken
            });
        }
        catch (e) {
            dispatch('reportException', e);
        }
    },
    async fetchTimezone({commit, dispatch}) {
        let timeZoneOffset = getOffset();
        if (!timeZoneOffset) {
            try {
                const timeZone = await (repositories.get(RepositoryFabric.SETTING) as SettingRepository).timezones();
                if (timeZone) {
                    timeZoneOffset = timeZone;
                    saveOffset(timeZoneOffset);
                }
            }
            catch (e) {
                dispatch('reportError', e)
            }
        }
        if (timeZoneOffset) {
            commit('setOffset', timeZoneOffset.defaultTimeZone.offset);
            commit('setTimeZoneNames', timeZoneOffset.defaultTimeZone.title);
        }
    },

    async reportException({state, dispatch}, e: Error)
    {
        Sentry.setUser({"id": state.session.userId.toString()});
        Sentry.setExtra("project", state.session.backend || '');
        Sentry.captureException(e);
        if (process.env.NODE_ENV === 'development') {
            console.warn(e);
        }

        if (state.initializing) {
            dispatch('addInitError', e);
        }
    },

    async reportError({state, dispatch}, error: string | { message?: string })
    {
        Sentry.setUser({"id": state.session.userId.toString()});
        Sentry.setExtra("project", state.session.backend || '');

        if (typeof error === "object" && error.message) {
            Sentry.captureMessage(error.message);
        }
        else if (typeof error === "string" || !error.message) {
            Sentry.captureMessage(error as string);
        }

        if (process.env.NODE_ENV === 'development') {
            console.error(error);
        }

        //hack to exclude ga errors
        if (state.initializing && !Object.prototype.hasOwnProperty.call(error, 'ga')) {
            dispatch('addInitError', error);
        }
    },

    async startProfileReloading({state, dispatch, commit}) {
        await (repositories.get(RepositoryFabric.USER) as UserRepository).ownProfile().then((profile) => {
            commit('setSessionProfile', profile);
        });

        if (!state.session.profile?.id) {
            return;
        }

        state.profileObserver = {
            [`object.UserProfiles.${state.session.profile?.id}.${state.session.language}`]: {
                [SignalRMethod.objectUpdated]: (data: any) => {
                    if (data && Array.isArray(data) && data.length) {
                        const updatedProfileData = parseJson(data.pop());
                        updatedProfileData?.data && dispatch('setSessionProfile', new UserProfile(updatedProfileData.data));
                    }
                }
            }
        }

        state.session.profile?.id && (repositories.get(RepositoryFabric.REALTIME) as RealtimeRepository).attach(state.profileObserver);
    },
    startConfigurationReloading({state, dispatch}, backend: string) {
        dispatch('services/checkUpdates', backend);
        state.configurationReloadIntervalId = window.setInterval(() => {
            dispatch('services/checkUpdates', backend);
        }, 60000);
    },

    stopMeetingsChecking({state}) {
        state.meetingsCheckingIntervalId && clearInterval(state.meetingsCheckingIntervalId);
    },

    stopProfileReloading({state}) {
        state.profileObserver && (repositories.get(RepositoryFabric.REALTIME) as RealtimeRepository).detach(state.profileObserver)
    },

    stopConfigurationReloading({state}) {
        state.configurationReloadIntervalId && clearInterval(state.configurationReloadIntervalId);
    },

    async clearIntervals({dispatch}) {
        // dispatch('stopProfileReloading');
        dispatch('stopMeetingsChecking');
        dispatch('stopConfigurationReloading');
        dispatch('onlineUsers/stopTracking');
        dispatch('points/stopTracking');
    },

    removeLocalStorageItems({dispatch}) {
        dispatch('expertRatings/removeLocalStorageItems');
    },

    setWebConferenceRecordingEventId({commit}, value: string) {
        commit('setWebConferenceRecordingEventId', value);
    },
    addInitError({state,commit}, error: Error | string) {
        const newInitErrors = state.initErrors && this.state.initErrors.length ? [...state.initErrors, error] : [error];
        commit('setInitErrors', newInitErrors)
    },
    setIntefaceState({ state }) {
        state.isOnEditing = false;
        const interfaceState = parseJson(localStorage.getItem(interfaceStateKey(state.session.userId, state.session.backend)))
        if (interfaceState) {
            state.isOnEditing = interfaceState.isOnEditing || false;
        }
    },
    toggleIsOnEditing({ state, getters, dispatch }) {
        if (getters.isAdmin) {
            state.isOnEditing = !state.isOnEditing;
            dispatch(mainActions.setInterfaceStateToLS);
        }
    },
    onIsOnEditing({ state, getters, dispatch }) {
        if (getters.isAdmin && !state.isOnEditing) {
            state.isOnEditing = true;
            dispatch(mainActions.setInterfaceStateToLS);
        }
    },
    offIsOnEditing({ state, getters, dispatch }) {
        if (getters.isAdmin && state.isOnEditing) {
            state.isOnEditing = false;
            dispatch(mainActions.setInterfaceStateToLS);
        }
    },
    setInterfaceStateToLS({ state }) {
        localStorage.setItem(interfaceStateKey(state.session.userId, state.session.backend), JSON.stringify({ isOnEditing: state.isOnEditing }));
    },
    setCurrentUserCompanyId({ commit }, payload: string) {
        commit("setCurrentUserCompanyId", payload);
    },
    setSelectedTimeZone({ commit, state }, payload: string) {

        let selectedTimeZone = null;

        if (payload || typeof payload === "string") {
            const offset = parseInt(payload);
            selectedTimeZone = !isNaN(offset) ? offset : null;
        }
        else {
            selectedTimeZone = state.timeZoneOffset as number;
        }

        setTimezoneToLS(payload, getTimezoneLSKey(state.session.backend));

        commit("setSelectedTimeZone", selectedTimeZone);
    },
    /**
     * Restoring selected timezone
     */
    initSelectedTimeZone({ state, dispatch }) {
        dispatch(mainActions.setSelectedTimeZone, getTimezoneFromLS(getTimezoneLSKey(state.session.backend)));
    },
    setAnonymAuthVisibility({ state }, payload: boolean) {
        state.showAnonymAuth = payload;
    }
};
